/**
 * Collapse repeated steps into one row each, without reordering the turn.
 *
 * A turn that searches three times and reads two pages produced five identical
 * lines, which reads as noise rather than progress. Grouping *consecutive* rows
 * with the same label — rather than all rows with that label — keeps the order
 * the work actually happened in, so "searched three times, then read two pages"
 * still reads as a sequence.
 *
 * The label is already curated per tool by the server (`tool_stage_labels`), so
 * this needs no new contract field and no classification of its own. That also
 * keeps it honest: a group describes calls that ran, and nothing more.
 */

const OUTCOME = { failed: 'failed', running: 'running' }

const outcomeOf = (row) => OUTCOME[row.status] ?? 'succeeded'

export function stepGroups(rows = []) {
  const groups = []
  for (const row of rows) {
    const last = groups[groups.length - 1]
    if (last && last.label === row.label) {
      last.rows.push(row)
    } else {
      groups.push({ id: row.id, label: row.label, rows: [row] })
    }
  }
  return groups.map((group) => {
    const counts = group.rows.reduce((acc, row) => {
      const key = outcomeOf(row)
      return { ...acc, [key]: (acc[key] ?? 0) + 1 }
    }, {})
    return {
      ...group,
      count: group.rows.length,
      counts,
      // A group is still running while any call in it is; otherwise a single
      // failure is worth surfacing over a majority of successes.
      status: counts.running ? 'running' : counts.failed ? 'failed' : 'succeeded',
    }
  })
}

/** "3 succeeded · 2 failed", or null when there is nothing worth adding. */
export function stepGroupSummary(group) {
  if (group.count < 2 && !group.counts.failed) return null
  const parts = []
  if (group.counts.succeeded) parts.push(`${group.counts.succeeded} succeeded`)
  if (group.counts.failed) parts.push(`${group.counts.failed} failed`)
  if (group.counts.running) parts.push(`${group.counts.running} running`)
  return parts.join(' · ') || null
}
